import { Button, Heading, Input, Modal, Spinner, Switch, Tag, Text } from "@ttc3k/trekker";
import { NavArrowLeft } from "iconoir-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Flex } from "styled-system/jsx";
import { useBookerContext, useCartContext, useInvoiceAdminBilling } from "widgets/booker/contexts";
import {
  EnumOwnerType,
  EnumUserAccessOperatorsContactRoles,
  EnumUserAccessOperatorsRole,
  useOperatorByIdQuery,
  useOperatorManyBillingContactSearchLazyQuery,
  usePeopleManySimpleLazyQuery,
} from "gql/generated";
import { buildNameEmailSearchQuery, getMultilingualString } from "shared/utils";
import { SelectableMediumSquareButton } from "shared/components/atoms";
import { addressToAddressSearchValue } from "shared/components/organisms/SurveyQuestionInput/addressSearchResponse";

type InvoiceBillingFlyupProps = {
  isOpen: boolean;
  onClose: () => void;
};

type StorefrontFlyupMode = "search" | "manual";

export const InvoiceBillingFlyup = ({
  isOpen,
  onClose,
}: InvoiceBillingFlyupProps) => {
  const { storefrontId, organizationId } = useBookerContext();
  const { updateOperatorId, updateCustomerAddress, businessField, updateBusinessField } =
    useCartContext();
  const {
    recipientContact,
    setRecipientContact,
    setRecipientPersonId,
    setShowBillingFields,
  } = useInvoiceAdminBilling();

  const { t } = useTranslation("booker", { keyPrefix: "INVOICE_BILLING" });

  const isOrgNested = Boolean(organizationId);

  const [listingSearch, setListingSearch] = useState("");
  const [selectedOperatorEntityId, setSelectedOperatorEntityId] = useState<
    string | null
  >(null);
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);

  const [storefrontMode, setStorefrontMode] =
    useState<StorefrontFlyupMode>("search");
  const [recipientSearch, setRecipientSearch] = useState("");
  const [billingContactsOnly, setBillingContactsOnly] = useState(false);
  const [manualDraft, setManualDraft] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    businessName: "",
  });
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen) {
      if (!wasOpenRef.current) {
        setListingSearch("");
        setSelectedOperatorEntityId(null);
        setSelectedStaffId(null);
        setRecipientSearch("");
        setBillingContactsOnly(false);
        setSelectedPersonId(null);
        setManualDraft({
          firstName: recipientContact.firstName,
          lastName: recipientContact.lastName,
          email: recipientContact.email,
          phone: recipientContact.phone,
          businessName: businessField.businessName ?? "",
        });
        setStorefrontMode(storefrontId ? "search" : "manual");
      }
      wasOpenRef.current = true;
    } else {
      wasOpenRef.current = false;
    }
  }, [isOpen, storefrontId, recipientContact, businessField]);

  const [searchOperators, { data: operatorSearchData, loading: operatorsLoading }] =
    useOperatorManyBillingContactSearchLazyQuery();

  useEffect(() => {
    if (!isOpen || !isOrgNested || !organizationId) return;
    const q = listingSearch.trim();
    if (q.length < 2) return;
    const handle = window.setTimeout(() => {
      void searchOperators({
        variables: {
          organizationId,
          search: q,
          limit: 50,
        },
      });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [isOpen, isOrgNested, organizationId, listingSearch, searchOperators]);

  const filteredListings =
    operatorSearchData?.operatorManyBillingContactSearch ?? [];

  // People visible to the organization directly (not necessarily tied to any
  // listing), searched with the same text as the listing search above so
  // admins can find and bill a person without needing to know which listing
  // (if any) they're connected to.
  const [searchOrgPeople, { data: orgPeopleSearchData, loading: orgPeopleLoading }] =
    usePeopleManySimpleLazyQuery();

  useEffect(() => {
    if (!isOpen || !isOrgNested || !organizationId) return;
    const q = listingSearch.trim();
    if (q.length < 2) return;
    const handle = window.setTimeout(() => {
      void searchOrgPeople({
        variables: {
          filter: {
            visibleTo: [
              {
                entity: {
                  type: EnumOwnerType.Organization,
                  entityId: organizationId,
                },
              },
            ],
          },
          queryBuilder: JSON.stringify(buildNameEmailSearchQuery(q)),
          limit: 20,
          skip: 0,
        },
      });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [isOpen, isOrgNested, organizationId, listingSearch, searchOrgPeople]);

  const orgPeopleSearchResults = orgPeopleSearchData?.peopleManySimple ?? [];

  const { data: operatorDetailData, loading: operatorDetailLoading } =
    useOperatorByIdQuery({
      variables: { operatorId: selectedOperatorEntityId! },
      skip: !isOpen || !selectedOperatorEntityId,
    });

  const selectedListingName = useMemo(() => {
    if (!selectedOperatorEntityId) return "";
    const selectedFromList = filteredListings.find(
      op => String(op._entityId) === selectedOperatorEntityId,
    );
    if (selectedFromList) {
      return getMultilingualString(selectedFromList.name ?? {});
    }
    return getMultilingualString(operatorDetailData?.operatorById?.name ?? {});
  }, [
    selectedOperatorEntityId,
    filteredListings,
    operatorDetailData?.operatorById?.name,
  ]);

  const staffList = operatorDetailData?.operatorById?.staff ?? [];

  const [searchPeople, { data: peopleSearchData }] =
    usePeopleManySimpleLazyQuery();

  useEffect(() => {
    if (!isOpen || !storefrontId || storefrontMode !== "search") return;
    if (recipientSearch.trim().length < 2) return;
    const handle = window.setTimeout(() => {
      void searchPeople({
        variables: {
          filter: {
            visibleTo: [
              {
                entity: {
                  type: EnumOwnerType.Storefront,
                  entityId: storefrontId,
                },
              },
            ],
          },
          queryBuilder: JSON.stringify(
            buildNameEmailSearchQuery(recipientSearch.trim()),
          ),
          limit: 20,
          skip: 0,
        },
      });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [isOpen, recipientSearch, searchPeople, storefrontId, storefrontMode]);

  const peopleSearchResults = useMemo(() => {
    const raw = peopleSearchData?.peopleManySimple ?? [];
    if (!billingContactsOnly) return raw;
    return raw.filter(person =>
      (person.tags ?? []).some(tag =>
        getMultilingualString(tag?.name ?? {})
          .toLowerCase()
          .includes("billing"),
      ),
    );
  }, [peopleSearchData?.peopleManySimple, billingContactsOnly]);

  const applyAndClose = () => {
    if (storefrontMode === "manual") {
      if (
        !manualDraft.firstName.trim() ||
        !manualDraft.email.trim()
      ) {
        return;
      }
      setRecipientPersonId(null);
      setRecipientContact({
        firstName: manualDraft.firstName.trim(),
        lastName: manualDraft.lastName.trim(),
        email: manualDraft.email.trim(),
        phone: manualDraft.phone.trim(),
      });
      updateBusinessField({
        ...businessField,
        businessName: manualDraft.businessName.trim(),
      });
      setShowBillingFields(true);
      onClose();
      return;
    }

    if (isOrgNested && selectedOperatorEntityId) {
      const staff = staffList.find(s => s._id === selectedStaffId);
      if (!staff?.email) return;
      updateOperatorId(selectedOperatorEntityId);
      setRecipientPersonId(null);
      setRecipientContact({
        firstName: staff.firstName ?? "",
        lastName: staff.lastName ?? "",
        email: staff.email ?? "",
        phone: "",
      });
      setShowBillingFields(true);
      onClose();
      return;
    }

    if (isOrgNested && !selectedOperatorEntityId && selectedPersonId) {
      const person = orgPeopleSearchResults.find(p => p._id === selectedPersonId);
      if (!person) return;
      updateOperatorId(null);
      setRecipientPersonId(person._id);
      setRecipientContact({
        firstName: person.firstName ?? "",
        lastName: person.lastName ?? "",
        email: person.email ?? "",
        phone: person.phone ?? "",
        address: addressToAddressSearchValue(person.address),
      });
      updateCustomerAddress(addressToAddressSearchValue(person.address));
      setShowBillingFields(true);
      onClose();
      return;
    }

    if (!isOrgNested) {
      if (selectedPersonId) {
        const person = peopleSearchResults.find(p => p._id === selectedPersonId);
        if (!person) return;
        setRecipientPersonId(person._id);
        setRecipientContact({
          firstName: person.firstName ?? "",
          lastName: person.lastName ?? "",
          email: person.email ?? "",
          phone: person.phone ?? "",
          address: addressToAddressSearchValue(person.address),
        });
        updateCustomerAddress(addressToAddressSearchValue(person.address));
        setShowBillingFields(true);
        onClose();
        return;
      }
    }
  };

  const canApplyOrgListing = Boolean(
    selectedOperatorEntityId &&
      selectedStaffId &&
      staffList.some(s => s._id === selectedStaffId && s.email),
  );

  const canApplyOrgPerson = Boolean(
    !selectedOperatorEntityId &&
      selectedPersonId &&
      orgPeopleSearchResults.some(p => p._id === selectedPersonId),
  );

  const canApplyOrg = canApplyOrgListing || canApplyOrgPerson;

  const canApplyStorefrontSearch = Boolean(
    selectedPersonId &&
      peopleSearchResults.some(p => p._id === selectedPersonId),
  );

  const canApplyManual =
    manualDraft.firstName.trim().length > 0 &&
    manualDraft.email.trim().length > 0;

  const canApply =
    storefrontMode === "manual"
      ? canApplyManual
      : isOrgNested
        ? canApplyOrg
        : canApplyStorefrontSearch;

  return (
    <Modal
      open={isOpen}
      placement="rightLargePane"
      onOpenChange={onClose}
      closeTriggerVisual="light"
      portal
    >
      <Flex flexDir="column" h="full" maxH="100dvh" minH="0">
        <Flex
          flexDir="column"
          gap="100"
          px="400"
          pt="400"
          pb="200"
          borderBottomWidth="1px"
          borderColor="border.light"
          flexShrink={0}
        >
          <Heading size="h5">{t("FLYUP_TITLE")}</Heading>
          <Text visual="smallRegular" color="text.mid">
            {t("FLYUP_SUBTITLE")}
          </Text>
        </Flex>

        <Flex flexDir="column" flex="1" minH="0" overflowY="auto" px="400" py="300">
          {isOrgNested ? (
            <Flex flexDir="column" gap="250" flex="1" minH="0">
              <Flex gap="100" flexWrap="wrap" w="100%">
                <SelectableMediumSquareButton
                  type="button"
                  aria-selected={storefrontMode === "search"}
                  flex="1"
                  minW="120px"
                  onClick={() => {
                    setStorefrontMode("search");
                    setSelectedOperatorEntityId(null);
                    setSelectedStaffId(null);
                    setSelectedPersonId(null);
                  }}
                >
                  <Text visual="smallSemiBold">{t("TAB_SEARCH")}</Text>
                </SelectableMediumSquareButton>
                <SelectableMediumSquareButton
                  type="button"
                  aria-selected={storefrontMode === "manual"}
                  flex="1"
                  minW="120px"
                  onClick={() => {
                    setStorefrontMode("manual");
                    setSelectedOperatorEntityId(null);
                    setSelectedStaffId(null);
                    setSelectedPersonId(null);
                  }}
                >
                  <Text visual="smallSemiBold">{t("TAB_MANUAL")}</Text>
                </SelectableMediumSquareButton>
              </Flex>

              {storefrontMode === "search" && !selectedOperatorEntityId ? (
                <Flex flexDir="column" gap="150" flex="1" minH="0">
                  <Input
                    value={listingSearch}
                    onChange={e => setListingSearch(e.target.value)}
                    label={t("DIRECTORY_LISTING_LABEL")}
                    placeholder={t("DIRECTORY_LISTING_PLACEHOLDER")}
                  />
                  {listingSearch.trim().length < 2 ? (
                    <Text color="text.mid" visual="smallRegular">
                      {t("DIRECTORY_LISTING_HINT")}
                    </Text>
                  ) : operatorsLoading || orgPeopleLoading ? (
                    <Spinner width="32px" height="32px" />
                  ) : (
                    <Box
                      borderWidth="1px"
                      borderColor="border.light"
                      borderRadius="md"
                      p="150"
                      flex="1"
                      minH="0"
                      overflowY="auto"
                    >
                      <Flex flexDir="column" gap="200">
                        <Flex flexDir="column" gap="100">
                          <Text visual="smallMedium" color="text.light">
                            {t("SECTION_LISTINGS")}
                          </Text>
                          {filteredListings.map(op => {
                            const extraStaffCount =
                              op.staffCount - op.previewStaff.length;
                            return (
                              <SelectableMediumSquareButton
                                key={op._entityId}
                                type="button"
                                alignSelf="stretch"
                                w="100%"
                                onClick={() => {
                                  setSelectedOperatorEntityId(
                                    String(op._entityId),
                                  );
                                  setSelectedStaffId(null);
                                  setSelectedPersonId(null);
                                }}
                              >
                                <Flex
                                  flexDir="column"
                                  alignItems="flex-start"
                                  gap="75"
                                  w="100%"
                                >
                                  <Text visual="smallSemiBold" textAlign="left">
                                    {getMultilingualString(op.name ?? {})}
                                  </Text>
                                  {op.previewStaff.length > 0 && (
                                    <Flex gap="75" flexWrap="wrap">
                                      {op.previewStaff.map(staffMember => (
                                        <Tag
                                          key={staffMember._id}
                                          size="sm"
                                          visual="primary"
                                          mode="light"
                                        >
                                          {staffMember.firstName}{" "}
                                          {staffMember.lastName}
                                        </Tag>
                                      ))}
                                      {extraStaffCount > 0 && (
                                        <Tag size="sm" visual="primary" mode="light">
                                          {t("MORE_STAFF", {
                                            count: extraStaffCount,
                                          })}
                                        </Tag>
                                      )}
                                    </Flex>
                                  )}
                                </Flex>
                              </SelectableMediumSquareButton>
                            );
                          })}
                          {!filteredListings.length && (
                            <Text color="text.mid" visual="smallRegular">
                              {t("DIRECTORY_NO_LISTINGS")}
                            </Text>
                          )}
                        </Flex>

                        <Flex flexDir="column" gap="100">
                          <Text visual="smallMedium" color="text.light">
                            {t("SECTION_PEOPLE")}
                          </Text>
                          {orgPeopleSearchResults.map(person => (
                            <SelectableMediumSquareButton
                              key={person._id}
                              type="button"
                              aria-selected={selectedPersonId === person._id}
                              alignSelf="stretch"
                              w="100%"
                              onClick={() => {
                                setSelectedPersonId(person._id);
                                setSelectedOperatorEntityId(null);
                                setSelectedStaffId(null);
                              }}
                            >
                              <Flex
                                flexDir="column"
                                alignItems="flex-start"
                                gap="50"
                              >
                                <Text visual="smallSemiBold">
                                  {person.firstName} {person.lastName}
                                </Text>
                                <Text visual="smallRegular" color="text.mid">
                                  {person.email}
                                </Text>
                              </Flex>
                            </SelectableMediumSquareButton>
                          ))}
                          {!orgPeopleSearchResults.length && (
                            <Text color="text.mid" visual="smallRegular">
                              {t("DIRECTORY_NO_PEOPLE")}
                            </Text>
                          )}
                        </Flex>
                      </Flex>
                    </Box>
                  )}
                </Flex>
              ) : storefrontMode === "search" ? (
                <Flex flexDir="column" gap="150" flex="1" minH="0">
                  <Button
                    mode="light"
                    size="sm"
                    alignSelf="flex-start"
                    Icon={NavArrowLeft}
                    onClick={() => {
                      setSelectedOperatorEntityId(null);
                      setSelectedStaffId(null);
                    }}
                  >
                    {t("BACK_TO_LISTINGS")}
                  </Button>
                  <Text visual="smallSemiBold">
                    {selectedListingName
                      ? t("SELECT_STAFF_FROM", { name: selectedListingName })
                      : t("SELECT_STAFF")}
                  </Text>
                  {operatorDetailLoading ? (
                    <Spinner width="32px" height="32px" />
                  ) : (
                    <Box flex="1" minH="0" overflowY="auto">
                      <Flex flexDir="column" gap="100">
                        {staffList.map(member => {
                          const operatorAccess = member.access?.operators?.find(
                            access =>
                              String(access?.entityId) ===
                              selectedOperatorEntityId,
                          );
                          const isOwner =
                            operatorAccess?.role ===
                            EnumUserAccessOperatorsRole.Owner;
                          const contactRoles = (
                            operatorAccess?.contactRoles ?? []
                          ).filter(
                            (
                              contactRole,
                            ): contactRole is EnumUserAccessOperatorsContactRoles =>
                              !!contactRole,
                          );

                          return (
                            <SelectableMediumSquareButton
                              key={member._id}
                              type="button"
                              aria-selected={selectedStaffId === member._id}
                              alignSelf="stretch"
                              w="100%"
                              disabled={!member.email}
                              onClick={() => setSelectedStaffId(member._id)}
                            >
                              <Flex
                                flexDir="column"
                                alignItems="flex-start"
                                gap="50"
                              >
                                <Text visual="smallSemiBold">
                                  {member.firstName} {member.lastName}
                                </Text>
                                <Text visual="smallRegular" color="text.mid">
                                  {member.email || t("STAFF_NO_EMAIL")}
                                </Text>
                                {(isOwner || contactRoles.length > 0) && (
                                  <Flex gap="75" flexWrap="wrap" mt="50">
                                    {isOwner && (
                                      <Tag size="sm" visual="success" mode="light">
                                        {t("TAG_OWNER")}
                                      </Tag>
                                    )}
                                    {contactRoles.map(contactRole => (
                                      <Tag
                                        key={contactRole}
                                        size="sm"
                                        visual={
                                          contactRole ===
                                          EnumUserAccessOperatorsContactRoles.PrimaryContact
                                            ? "accent.blue"
                                            : "accent.purple"
                                        }
                                        mode="light"
                                      >
                                        {contactRole ===
                                        EnumUserAccessOperatorsContactRoles.PrimaryContact
                                          ? t("TAG_PRIMARY_CONTACT")
                                          : t("TAG_FINANCIAL_CONTACT")}
                                      </Tag>
                                    ))}
                                  </Flex>
                                )}
                              </Flex>
                            </SelectableMediumSquareButton>
                          );
                        })}
                        {!staffList.length && (
                          <Text color="text.mid" visual="smallRegular">
                            {t("NO_STAFF")}
                          </Text>
                        )}
                      </Flex>
                    </Box>
                  )}
                </Flex>
              ) : (
                <Flex flexDir="column" gap="200">
                  <Input
                    value={manualDraft.businessName}
                    onChange={e =>
                      setManualDraft(d => ({
                        ...d,
                        businessName: e.target.value,
                      }))
                    }
                    label={t("FORM.BUSINESS_NAME")}
                  />
                  <Input
                    value={manualDraft.firstName}
                    onChange={e =>
                      setManualDraft(d => ({
                        ...d,
                        firstName: e.target.value,
                      }))
                    }
                    label={t("FORM.FIRST_NAME")}
                  />
                  <Input
                    value={manualDraft.lastName}
                    onChange={e =>
                      setManualDraft(d => ({
                        ...d,
                        lastName: e.target.value,
                      }))
                    }
                    label={t("FORM.LAST_NAME")}
                  />
                  <Input
                    value={manualDraft.email}
                    onChange={e =>
                      setManualDraft(d => ({ ...d, email: e.target.value }))
                    }
                    label={t("FORM.EMAIL")}
                    type="email"
                  />
                  <Input
                    value={manualDraft.phone}
                    onChange={e =>
                      setManualDraft(d => ({ ...d, phone: e.target.value }))
                    }
                    label={t("FORM.PHONE")}
                  />
                </Flex>
              )}
            </Flex>
          ) : (
            <Flex flexDir="column" gap="250" flex="1" minH="0">
              {storefrontId ? (
                <Flex gap="100" flexWrap="wrap" w="100%">
                  <SelectableMediumSquareButton
                    type="button"
                    aria-selected={storefrontMode === "search"}
                    flex="1"
                    minW="120px"
                    onClick={() => {
                      setStorefrontMode("search");
                      setSelectedPersonId(null);
                    }}
                  >
                    <Text visual="smallSemiBold">{t("TAB_SEARCH")}</Text>
                  </SelectableMediumSquareButton>
                  <SelectableMediumSquareButton
                    type="button"
                    aria-selected={storefrontMode === "manual"}
                    flex="1"
                    minW="120px"
                    onClick={() => {
                      setStorefrontMode("manual");
                      setSelectedPersonId(null);
                    }}
                  >
                    <Text visual="smallSemiBold">{t("TAB_MANUAL")}</Text>
                  </SelectableMediumSquareButton>
                </Flex>
              ) : null}

              {storefrontMode === "search" && storefrontId && (
                <Flex flexDir="column" gap="150" flex="1" minH="0">
                  <Input
                    value={recipientSearch}
                    onChange={e => setRecipientSearch(e.target.value)}
                    label={t("SEARCH_LABEL")}
                    placeholder={t("SEARCH_PLACEHOLDER")}
                  />
                  <Flex alignItems="center" gap="200">
                    <Switch
                      checked={billingContactsOnly}
                      onCheckedChange={e =>
                        setBillingContactsOnly(!!e?.checked)
                      }
                    />
                    <Text visual="smallRegular">{t("BILLING_TAG_FILTER")}</Text>
                  </Flex>
                  {peopleSearchResults.length > 0 &&
                    recipientSearch.trim().length >= 2 && (
                      <Box flex="1" minH="0" overflowY="auto">
                        <Flex flexDir="column" gap="100">
                          {peopleSearchResults.map(person => (
                            <SelectableMediumSquareButton
                              key={person._id}
                              type="button"
                              aria-selected={selectedPersonId === person._id}
                              alignSelf="stretch"
                              w="100%"
                              onClick={() => setSelectedPersonId(person._id)}
                            >
                              <Flex
                                flexDir="column"
                                alignItems="flex-start"
                                gap="50"
                              >
                                <Text visual="smallSemiBold">
                                  {person.firstName} {person.lastName}
                                </Text>
                                <Text visual="smallRegular" color="text.mid">
                                  {person.email}
                                </Text>
                              </Flex>
                            </SelectableMediumSquareButton>
                          ))}
                        </Flex>
                      </Box>
                    )}
                </Flex>
              )}

              {storefrontMode === "manual" && (
                <Flex flexDir="column" gap="200">
                  <Input
                    value={manualDraft.businessName}
                    onChange={e =>
                      setManualDraft(d => ({
                        ...d,
                        businessName: e.target.value,
                      }))
                    }
                    label={t("FORM.BUSINESS_NAME")}
                  />
                  <Input
                    value={manualDraft.firstName}
                    onChange={e =>
                      setManualDraft(d => ({
                        ...d,
                        firstName: e.target.value,
                      }))
                    }
                    label={t("FORM.FIRST_NAME")}
                  />
                  <Input
                    value={manualDraft.lastName}
                    onChange={e =>
                      setManualDraft(d => ({
                        ...d,
                        lastName: e.target.value,
                      }))
                    }
                    label={t("FORM.LAST_NAME")}
                  />
                  <Input
                    value={manualDraft.email}
                    onChange={e =>
                      setManualDraft(d => ({ ...d, email: e.target.value }))
                    }
                    label={t("FORM.EMAIL")}
                    type="email"
                  />
                  <Input
                    value={manualDraft.phone}
                    onChange={e =>
                      setManualDraft(d => ({ ...d, phone: e.target.value }))
                    }
                    label={t("FORM.PHONE")}
                  />
                </Flex>
              )}
            </Flex>
          )}
        </Flex>

        <Flex
          gap="150"
          justifyContent="flex-end"
          px="400"
          py="300"
          borderTopWidth="1px"
          borderColor="border.light"
          flexShrink={0}
        >
          <Button mode="light" onClick={onClose}>
            {t("FLYUP_CANCEL")}
          </Button>
          <Button onClick={() => void applyAndClose()} disabled={!canApply}>
            {t("FLYUP_SAVE")}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
